import React, { useMemo, useState } from "react";
import { BoxScoreProps } from "./types";
import { Competitor } from "../Competitor/Competitor";
import { BoxScoreDetails } from "./components/BoxScoreDetails";
import { BoxScoreSkeleton } from "./components/BoxScoreSkeleton";
import { normalizeSport } from "shared/src/utils/sportMap";
import cx from "components/src/utils/cx";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import duration from "dayjs/plugin/duration";
import get from "lodash-es/get";
import { useTeamColors } from "shared/src/hooks/useTeamColors";
import { useCompetitionType } from "shared/src/hooks/useCompetitionType";
import { useSize } from "shared/src/hooks/useSize";
import { mapperFactory as eventDetailsToCompetitorSyncMapper } from "../MarketTemplate/js/prepareMarkets/mappers/mapCompetitorSync";
import { makeAriaLabel } from "./js/makeAriaLabel";
import { LiveStreamIcon } from "./components/LiveStreamIcon";
import { randomIdString } from "shared/src/utils/randomIdString";
import { kebabCase } from "shared/src/utils/kebabCase";
import omit from "lodash-es/omit";
import { EventDetails } from "../EventDetails/EventDetails";
import parseIconString, {
  IconStringReturnType,
} from "components/src/utils/parseIconString";
import { isValidIconString } from "components/src/utils/parseIconString";
import { BoxScoreMinimal } from "./components/BoxScoreMinimal";
import { BoxScoreV2 } from "./components/BoxScoreV2";

dayjs.extend(isSameOrAfter);
dayjs.extend(duration);

export const genericCompetitor = { displayName: "--" };

const breakpoints = [
  // {
  //   minSize: 632,
  //   maxSize: Infinity,
  //   name: "shortname",
  //   textSize: "heading-md",
  //   breakpoint: "large",
  // },
  {
    minSize: 451,
    maxSize: Infinity,
    name: "abbreviation",
    textSize: "display-lg",
    iconSize: 58,
    scoreSize: "md",
    breakpoint: "small",
  },
  {
    minSize: 376,
    maxSize: 450,
    name: "abbreviation",
    iconSize: "3xl",
    textSize: "display-md",
    scoreSize: "md",
    breakpoint: "mobile",
  },
  {
    minSize: 0,
    maxSize: 375,
    name: "abbreviation",
    iconSize: 30,
    textSize: "display-md",
    scoreSize: "sm",
    breakpoint: "xs-mobile",
  },
] as const;

export const BoxScore = ({
  id = "",
  competitors,
  gameState,
  date,
  hasLiveStream = false,
  onClick,
  showCountdown = true,
  variant = "horizontal",
  skeleton,
  animation = true,
  sport: sportProp,
  competitionType,
  competition,
  league,
  competitionIcon,
  hideColorBars,
  eventDetails,
  hideExtendedDetails = false,
  minimal,
  height,
  syncMapper,
  patternOpacity,
  ...rest
}: BoxScoreProps) => {
  // Support deprecated 'league' prop
  const __competition = competition || league;
  const { sport } = useCompetitionType({ sport: sportProp, competitionType });
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { width } =
    useSize(ref, 10, false, { trailing: true, freeze: 20 }) ?? {};

  /**
   * Computed Props
   */

  const isPreGame = gameState === "pregame";
  const isLive = gameState === "live";

  const _showCountdown = showCountdown && !!date && isPreGame;

  const { secondaryEventDetails, secondaryEventDetailsSyncMapper } =
    useMemo(() => {
      const omitArr = ["gameState", "gameTime", "isLive", "startTime"];
      const isLive = gameState === "live";
      return {
        secondaryEventDetails: isLive ? omit(eventDetails, omitArr) : {},
        secondaryEventDetailsSyncMapper: (data) => omit(data, omitArr),
      };
    }, [eventDetails, gameState]);

  const { _competitorOne, _competitorTwo } = useMemo(() => {
    let _competitorOne = competitors
      ? get(competitors, 0, genericCompetitor)
      : genericCompetitor;
    let _competitorTwo = competitors
      ? get(competitors, 1, genericCompetitor)
      : genericCompetitor;

    // Parse Icon String
    _competitorOne = {
      ..._competitorOne,
      team: checkForIconString(_competitorOne?.team),
      icon: checkForIconString(_competitorOne?.icon),
    };
    _competitorTwo = {
      ..._competitorTwo,
      team: checkForIconString(_competitorTwo?.team),
      icon: checkForIconString(_competitorTwo?.icon),
    };

    const isSoccer = (normalizeSport(sport)) === "football";
    // Setup sync if it exists
    if (eventDetails && "sync" in eventDetails) {
      const syncHome = {
        subscribe: eventDetails?.sync?.subscribe,
        getSnapshot: eventDetails?.sync?.getSnapshot,
        mapper: eventDetailsToCompetitorSyncMapper(isSoccer ? "home" : "away"),
      };
      const syncAway = {
        subscribe: eventDetails?.sync?.subscribe,
        getSnapshot: eventDetails?.sync?.getSnapshot,
        mapper: eventDetailsToCompetitorSyncMapper(isSoccer ? "away" : "home"),
      };
      _competitorOne = { ..._competitorOne, sync: syncHome };
      _competitorTwo = { ..._competitorTwo, sync: syncAway };
    }
    return { _competitorOne, _competitorTwo };
  }, [competitors, eventDetails, eventDetails?.sync]);

  const firstCompetitor = _competitorOne?.team ?? _competitorOne?.displayName;
  const secondCompetitor = _competitorTwo?.team ?? _competitorTwo?.displayName;

  const teams = {
    first: useTeamColors(firstCompetitor),
    second: useTeamColors(secondCompetitor),
  };
  
  const _competition = useMemo(()=>{
    let derivedLeague = teams?.first?.league && teams?.second?.league;
    return derivedLeague ?? __competition ?? sport ?? null
  },[__competition, sport, teams]);

  const detailsProps = useMemo(
    () => ({
      date,
      hasLiveStream,
      variant,
      showCountdown: _showCountdown,
      gameState,
      eventDetails,
      minimal,
      _competition,
      competitionIcon,
      sport,
      syncMapper,
    }),
    [
      date,
      hasLiveStream,
      variant,
      _showCountdown,
      gameState,
      eventDetails,
      competitionIcon,
      sport,
      minimal,
      _competition
    ]
  );

  /**
   * Computed Id
   */
  const computedId = useMemo(
    () =>
      `cui__box_score_${randomIdString()}-${kebabCase(
        firstCompetitor
      )}-${kebabCase(secondCompetitor)}`,
    [firstCompetitor, secondCompetitor]
  );

  const _id = id ? id : computedId;

  /**
   * Breakpoints
   */

  const {
    name: _name,
    textSize: _textSize,
    iconSize,
    scoreSize,
    breakpoint,
  } = useMemo<{
    name: string;
    textSize: string;
    breakpoint: string;
    iconSize: string | number;
    scoreSize: string;
  }>(() => {
    const breakpointFilter = ({ minSize, maxSize }) => {
      return typeof width === "number" && width >= minSize && width <= maxSize;
    };
    const currentBreakpoint =
      breakpoints.filter(breakpointFilter).at(0) ?? breakpoints.at(0)!;
    return currentBreakpoint;
  }, [width]);

  const _showColorBars = useMemo(() => {
    return (
      !hideColorBars && breakpoint !== "mobile" && breakpoint !== "xs-mobile"
    );
  }, [breakpoint, hideColorBars]);

  // const useCityName =
  //   (teams?.first?.league === "college" ||
  //     teams?.second?.league === "college") &&
  //   breakpoint !== "mobile" && breakpoint !== "xs-mobile";

  const { ariaLabelPreGame, ariaLabel } = makeAriaLabel({
    date,
    _competitorOne,
    _competitorTwo,
    firstCompetitor,
    secondCompetitor,
    hasLiveStream,
    detailsProps,
    gameState,
  });

  const [isHorizontal, isVertical] = useMemo(() => {
    return [variant === "horizontal", variant === "vertical"];
  }, [variant]);

  const _hideScorePadding =
    breakpoint === "xs-mobile"
      ? true
      : gameState !== "pregame" && breakpoint === "mobile"
        ? true
        : false;

  const boxScoreProps = useMemo(() => {
    return {
      isVertical,
      isHorizontal,
      isPreGame,
      isLive,
      ariaLabelPreGame,
      ariaLabel,
      competitors,
      _id,
      variant,
      detailsProps,
      teams,
      breakpoint,
      hasLiveStream,
      _competition,
      secondaryEventDetailsSyncMapper,
      secondaryEventDetails,
      hideExtendedDetails,
      eventDetails,
      onClick,
      iconSize,
      scoreSize,
      _textSize,
      _competitorOne,
      _competitorTwo,
      _name,
      _showColorBars,
      animation,
      sport,
      competitionType,
      setRef,
      rest,
      _hideScorePadding,
      minimal,
      width,
      height,
      patternOpacity
    };
  }, [
    isVertical,
    isHorizontal,
    isPreGame,
    isLive,
    ariaLabelPreGame,
    ariaLabel,
    competitors,
    _id,
    variant,
    detailsProps,
    teams,
    breakpoint,
    hasLiveStream,
    secondaryEventDetailsSyncMapper,
    secondaryEventDetails,
    hideExtendedDetails,
    eventDetails,
    competition,
    onClick,
    iconSize,
    scoreSize,
    _textSize,
    _competitorOne,
    _competitorTwo,
    _name,
    _showColorBars,
    animation,
    sport,
    setRef,
    rest,
    _hideScorePadding,
    minimal,
    width,
    height,
    patternOpacity
  ]);

  if (skeleton)
    return <BoxScoreSkeleton orientation={variant} animation={animation} height={height} />;

  if (minimal || variant === "vertical") {
    return <BoxScoreMinimal {...boxScoreProps} />;
  }
  return <BoxScoreV2 {...boxScoreProps} />;
};

BoxScore.displayName = "BoxScore";

function checkForIconString(str) {
  if (isValidIconString(str)) {
    const iconStr = parseIconString(str) as IconStringReturnType;
    return iconStr && typeof iconStr === "object" ? iconStr.name : str;
  }
  return str;
}


